/**
 * Trigger and monitor the WellSky goal sync Temporal workflow for the agency.
 *
 * The backend derives the workflow id from the agency system name and today's
 * date, so this component never stores a job id: any user landing on this page
 * sees the status of today's run, whoever started it. Status is polled every
 * 60 seconds while the page is open.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@roger/ui-components';
import { ChevronDown, ChevronRight, Loader2, RefreshCw } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import { useStore } from '../../store';
import {
  getSyncGoalsForAgencyWorkflowStatus,
  startSyncGoalsForAgencyWorkflow,
} from '../../services/api';
import type { AgencyGoalSyncStatusResponse } from '../../services/api';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import { getHttpStatus } from '../../utils/httpErrorUtils';

const POLL_INTERVAL_MS = 60_000;

/** Shape of the workflow result dict (see SyncAgencyGoalsWorkflow.run). */
interface ClinicSyncResult {
  clinic_id?: string;
  clinic_name?: string | null;
  goals_count?: number;
  interventions_count?: number;
  /** Present when the clinic was skipped entirely. */
  error?: string;
  /** Per-record upsert errors for a clinic that otherwise synced. */
  errors?: string[];
}

interface GoalSyncResult {
  clinics_synced?: number;
  clinics_skipped?: number;
  clinics?: ClinicSyncResult[];
  docs_scrape_workflow_id?: string;
  docs_scrape_summary?: {
    total?: number;
    succeeded?: number;
    failed?: number;
  } | null;
}

/** Local status: workflow status name, or NOT_RUN when no sync exists today. */
type SyncStatus = string | 'NOT_RUN' | null;

const statusBadge = (status: SyncStatus): React.ReactNode => {
  switch (status) {
    case null:
      return <Badge variant='outline'>Checking…</Badge>;
    case 'NOT_RUN':
      return <Badge variant='outline'>Not run today</Badge>;
    case 'RUNNING':
      return (
        <Badge className='bg-blue-100 text-blue-800 whitespace-nowrap'>
          <Loader2 className='mr-1 h-3 w-3 animate-spin' />
          Running
        </Badge>
      );
    case 'COMPLETED':
      return <Badge className='bg-green-100 text-green-800'>Completed</Badge>;
    case 'FAILED':
    case 'TERMINATED':
    case 'CANCELED':
    case 'TIMED_OUT':
      return <Badge variant='destructive'>Failed</Badge>;
    default:
      return <Badge variant='outline'>{status}</Badge>;
  }
};

const GoalSyncSection: React.FC = () => {
  const { user, showError, showSuccess } = useStore(
    useShallow(state => ({
      user: state.user,
      showError: state.showError,
      showSuccess: state.showSuccess,
    }))
  );
  const agencySystemName = user?.agency?.system_name;

  const [status, setStatus] = useState<SyncStatus>(null);
  const [statusDetail, setStatusDetail] =
    useState<AgencyGoalSyncStatusResponse | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const [showRawResult, setShowRawResult] = useState(false);

  const fetchStatus = useCallback(async () => {
    if (!agencySystemName) return;
    try {
      const response = await getSyncGoalsForAgencyWorkflowStatus({
        query: { agency_system_name: agencySystemName },
        throwOnError: true,
      });
      setStatus(response.data.status);
      setStatusDetail(response.data);
    } catch (error) {
      if (getHttpStatus(error) === 404) {
        setStatus('NOT_RUN');
        setStatusDetail(null);
      } else {
        console.error('Failed to fetch goal sync status:', error);
      }
    }
  }, [agencySystemName]);

  useEffect(() => {
    fetchStatus();
    const timer = setInterval(fetchStatus, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [fetchStatus]);

  const handleStart = async () => {
    if (!agencySystemName) return;
    setIsStarting(true);
    try {
      await startSyncGoalsForAgencyWorkflow({
        query: { agency_system_name: agencySystemName },
        throwOnError: true,
      });
      showSuccess('Goal sync started');
      await fetchStatus();
    } catch (error) {
      console.error('Failed to start goal sync:', error);
      showError(getBusinessErrorMessage(error) ?? 'Failed to start goal sync');
      // A 409 means a run already exists today — refresh so the badge shows it.
      await fetchStatus();
    } finally {
      setIsStarting(false);
    }
  };

  const result =
    status === 'COMPLETED' && statusDetail?.result
      ? (statusDetail.result as GoalSyncResult)
      : null;
  const scrape = result?.docs_scrape_summary;

  return (
    <Card>
      <CardHeader>
        <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
          <div>
            <CardTitle>Goal Sync</CardTitle>
            <CardDescription>
              Scrape goals from WellSky into RogerDocs, then sync them into
              every clinic in this agency. Runs once per day; a failed run can
              be retried.
            </CardDescription>
          </div>
          {statusBadge(status)}
        </div>
      </CardHeader>
      <CardContent className='flex flex-col gap-2'>
        <div>
          <Button
            size='sm'
            onClick={handleStart}
            disabled={isStarting || !agencySystemName || status === 'RUNNING'}
          >
            {isStarting ? (
              <Loader2 className='mr-2 h-4 w-4 animate-spin' />
            ) : (
              <RefreshCw className='mr-2 h-4 w-4' />
            )}
            Sync Goals from WellSky
          </Button>
        </div>
        {result && (
          <div className='flex flex-col gap-2 text-sm'>
            <p className='text-muted-foreground'>
              {result.clinics_synced ?? 0} clinics synced,{' '}
              {result.clinics_skipped ?? 0} skipped
              {scrape &&
                ` — WellSky scrape: ${scrape.succeeded ?? 0}/${scrape.total ?? 0} clinics scraped` +
                  (scrape.failed ? `, ${scrape.failed} failed` : '')}
            </p>
            {(result.clinics ?? []).map(clinic => (
              <div
                key={clinic.clinic_id ?? clinic.clinic_name ?? ''}
                className='rounded-md border px-3 py-2'
              >
                <div className='flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between'>
                  <span className='font-medium'>
                    {clinic.clinic_name || clinic.clinic_id || 'Unnamed clinic'}
                  </span>
                  {clinic.error ? (
                    <Badge variant='destructive' className='whitespace-nowrap'>
                      Skipped
                    </Badge>
                  ) : (
                    <span className='text-muted-foreground'>
                      {clinic.goals_count ?? 0} goals,{' '}
                      {clinic.interventions_count ?? 0} interventions
                    </span>
                  )}
                </div>
                {clinic.error && (
                  <p className='mt-1 text-destructive'>{clinic.error}</p>
                )}
                {(clinic.errors ?? []).map((err, i) => (
                  <p key={i} className='mt-1 text-destructive'>
                    {err}
                  </p>
                ))}
              </div>
            ))}
            <button
              type='button'
              className='flex items-center gap-1 text-muted-foreground hover:text-foreground w-fit'
              onClick={() => setShowRawResult(prev => !prev)}
            >
              {showRawResult ? (
                <ChevronDown className='h-4 w-4' />
              ) : (
                <ChevronRight className='h-4 w-4' />
              )}
              Full result JSON
            </button>
            {showRawResult && (
              <pre className='max-h-96 overflow-auto rounded-md bg-muted p-3 text-xs'>
                {JSON.stringify(statusDetail?.result, null, 2)}
              </pre>
            )}
          </div>
        )}
        {statusDetail?.error && (
          <p className='text-sm text-destructive'>{statusDetail.error}</p>
        )}
      </CardContent>
    </Card>
  );
};

export default GoalSyncSection;
